import { useEffect, useMemo } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { ExpiryBanner } from '../components/ExpiryBanner.tsx';
import { Avatar } from '../components/Avatar.tsx';
import { NavIcon, type IconName } from '../components/NavIcon.tsx';
import { useCompanyAuth } from '../lib/auth/company.tsx';
import { BRAND } from '../lib/brand.ts';
import { companyApi, ApiError } from '../lib/api.ts';
import type { Company, Question, Submission } from '../lib/types.ts';
import { computeProgress } from '../lib/visibility.ts';
import { questionnaireActionItems } from '../lib/vendorActions.ts';

const NAV: { to: string; label: string; icon: IconName; end?: boolean }[] = [
  { to: '/c', label: 'Home', icon: 'home', end: true },
  { to: '/c/questionnaire', label: 'Questionnaires', icon: 'list-checks' },
  { to: '/c/documents', label: 'Documents', icon: 'file-text' },
  { to: '/c/info', label: 'Profile', icon: 'user' },
];

export function CompanyShell() {
  const { session, loading, signOut } = useCompanyAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: () => companyApi.get<{ company: Company }>('/api/me'),
    enabled: !!session,
  });

  // Task 16: action-needed badge on the Questionnaires nav item. Reuse the same
  // query keys as the Questionnaire wizard so the cache is shared.
  const questionsQuery = useQuery({
    queryKey: ['/api/me/questions'],
    queryFn: () => companyApi.get<{ questions: Question[] }>('/api/me/questions'),
    enabled: !!session,
  });
  const submissionsQuery = useQuery({
    queryKey: ['me', 'submissions'],
    queryFn: () => companyApi.get<{ submissions: Submission[] }>('/api/me/submissions'),
    enabled: !!session,
  });

  // Total questions the vendor still owes action on, across all questionnaires.
  // Returned/commented items always count; required+visible questions count as
  // soon as they are unanswered — so a freshly assigned questionnaire flags its
  // outstanding questions on the badge from first login, not only after submit.
  const questionnairePendingCount = useMemo(() => {
    const questions = questionsQuery.data?.questions ?? [];
    const submissions = submissionsQuery.data?.submissions ?? [];
    const subByQ = new Map(submissions.map((s) => [s.question_id, s]));
    const byQn = new Map<string, typeof questions>();
    for (const q of questions) {
      if (!q.questionnaire_id) continue;
      const list = byQn.get(q.questionnaire_id) ?? [];
      list.push(q);
      byQn.set(q.questionnaire_id, list);
    }
    let total = 0;
    for (const qs of byQn.values()) {
      const breakdown = computeProgress(qs, submissions);
      const visible = qs.filter((q) => breakdown.visibleIds.has(q.id));
      total += questionnaireActionItems(visible, subByQ, breakdown.completionByQuestion).length;
    }
    return total;
  }, [questionsQuery.data, submissionsQuery.data]);

  useEffect(() => {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      void signOut();
      navigate('/c/login', { replace: true });
    }
  }, [error, signOut, navigate]);

  if (loading) return <CenteredSpinner />;
  if (!session) {
    return <Navigate to="/c/login" state={{ from: location }} replace />;
  }
  if (isLoading) return <CenteredSpinner />;
  const company = data?.company;

  return (
    <div className="flex min-h-screen flex-col" style={{ background: '#F8FAFC' }}>
      {/* Top header */}
      <header className="border-b bg-white" style={{ borderColor: '#E2E8F0' }}>
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <img src={BRAND.logoUrl} alt={BRAND.logoAlt} className="h-8 w-auto" />
            <span className="hidden text-xs text-slate-400 sm:inline">Vendor Portal</span>
          </div>
          <div className="flex min-w-0 items-center gap-3">
            <div className="min-w-0 text-right">
              <div
                className="truncate text-[13px] font-semibold"
                style={{ color: 'var(--brand-primary)' }}
              >
                {company?.contact_name ?? 'Account'}
              </div>
              <div className="truncate text-[11px] text-slate-500">{company?.company_name}</div>
            </div>
            <Avatar name={company?.contact_name ?? 'U'} size={34} />
            <button
              type="button"
              onClick={() => void signOut()}
              className="rounded p-1.5 hover:bg-slate-100"
              aria-label="Sign out"
              title="Sign out"
            >
              <NavIcon name="log-out" size={17} style={{ color: '#64748B' }} />
            </button>
          </div>
        </div>
      </header>

      {company && <ExpiryBanner company={company} />}

      {/* pb clears the fixed bottom nav, including the iOS safe-area inset it absorbs */}
      <main
        className="mx-auto w-full max-w-3xl flex-1 px-4 pt-5"
        style={{ paddingBottom: 'calc(7rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <Outlet />
        <div
          className="font-mono-eyebrow mt-10 text-center text-[10px] uppercase tracking-[0.2em]"
          style={{ color: '#64748B' }}
        >
          Powered by{' '}
          <a
            href="https://www.locate2u.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: 'var(--brand-primary)' }}
          >
            Locate2u
          </a>
        </div>
      </main>

      {/* Bottom navigation */}
      <nav
        className="fixed inset-x-0 bottom-0 z-10 border-t bg-white"
        style={{ borderColor: '#E2E8F0', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        aria-label="Primary"
      >
        <div className="mx-auto flex max-w-3xl items-stretch">
          {NAV.map((item) => {
            const isQuestionnaires = item.label === 'Questionnaires';
            const showBadge = isQuestionnaires && questionnairePendingCount > 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? false}
                className="relative flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] font-medium"
                style={({ isActive }) =>
                  isActive ? { color: 'var(--brand-primary)' } : { color: '#64748B' }
                }
              >
                <span className="relative">
                  <NavIcon name={item.icon} size={18} />
                  {showBadge && (
                    <span className="absolute -right-2.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                      {questionnairePendingCount}
                    </span>
                  )}
                </span>
                {item.label}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
